import { Chart as ChartJS } from 'chart.js';

/**
 * Two small canvas plugins the QA charts need and Chart.js does not ship:
 * a threshold line and value labels.
 *
 * They exist here rather than as dependencies because chartjs-plugin-annotation
 * and chartjs-plugin-datalabels would each be a package for about twenty lines
 * of canvas work — and because these read the page's own colour tokens, which a
 * generic plugin cannot.
 *
 * Registration is global but both are inert unless a chart names them in its
 * options, so no other chart in the app changes.
 */

// Canvas cannot inherit a CSS variable, so the plugins read the page's tokens
// off the element they draw into. Without this the labels were a fixed slate
// grey — invisible on the dark theme, where the surface is nearly black.
export const tokenColor = (chart, name, fallback) => {
  try {
    const value = getComputedStyle(chart.canvas).getPropertyValue(name).trim();
    return value || fallback;
  } catch (error) {
    return fallback;
  }
};

export const qaTargetLine = {
  id: 'qaTargetLine',
  afterDatasetsDraw(chart, _args, options) {
    const value = Number(options?.value);
    if (!Number.isFinite(value)) return;

    const { ctx, chartArea, scales } = chart;
    const y = scales?.y?.getPixelForValue(value);
    if (!Number.isFinite(y) || y < chartArea.top || y > chartArea.bottom)
      return;

    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = tokenColor(
      chart,
      '--qa-text-muted',
      'rgba(107, 114, 128, 0.85)'
    );
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();

    if (options.label) {
      ctx.setLineDash([]);
      ctx.fillStyle = tokenColor(
        chart,
        '--qa-text-muted',
        'rgba(107, 114, 128, 0.95)'
      );
      ctx.font = '11px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(options.label, chartArea.right, y - 5);
    }
    ctx.restore();
  },
};

export const qaValueLabels = {
  id: 'qaValueLabels',
  afterDatasetsDraw(chart, _args, options) {
    if (!options?.enabled) return;

    const { ctx } = chart;
    ctx.save();
    ctx.font = '600 11px system-ui, -apple-system, sans-serif';
    ctx.fillStyle = tokenColor(chart, '--qa-text', 'rgba(55, 65, 81, 0.95)');
    ctx.textAlign = 'center';
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const points = chart.getDatasetMeta(datasetIndex).data;
      // A line with seven labels is noise; the endpoint is the number people
      // came for.
      const lastWithValue = dataset.data.reduce(
        (last, value, index) =>
          value === null || value === undefined ? last : index,
        -1
      );
      points.forEach((element, index) => {
        const raw = dataset.data[index];
        if (raw === null || raw === undefined) return;
        if (options.lastOnly && index !== lastWithValue) return;
        const text = options.format ? options.format(raw) : String(raw);
        ctx.fillText(text, element.x, element.y - 8);
      });
    });
    ctx.restore();
  },
};

export const registerQaChartPlugins = () => {
  ChartJS.register(qaTargetLine, qaValueLabels);
};
